import React, { useEffect } from 'react';
import './testamentoFacilLanding.css';

const APP_URL = 'https://testamentofacil.mx/';
const BLOG_URL = 'https://testamentofacil.mx/blog/';
const NOTARY_REGISTER_URL = 'https://apitestmx.mindware.com.mx/admin/register';

/* ── Recent blog posts (mirrors testamentofacil.mx/blog) ─────────── */
const BLOG_POSTS = [
  {
    title: '¿Cuánto cuesta un testamento en 2026?',
    date: '25 de julio de 2026',
    href: 'https://testamentofacil.mx/blog/cuanto-cuesta-un-testamento-2026/',
  },
  {
    title: 'Tipos de testamento en México y cuál te conviene',
    date: '25 de julio de 2026',
    href: 'https://testamentofacil.mx/blog/tipos-de-testamento-en-mexico/',
  },
  {
    title: 'Mes del testamento: descuentos y beneficios que debes conocer',
    date: '25 de julio de 2026',
    href: 'https://testamentofacil.mx/blog/mes-del-testamento-descuentos-beneficios/',
  },
];

/* ── How it works ─────────────────────────────────────────────── */
const STEPS = [
  {
    n: '1',
    title: 'Cuéntanos un poco de ti',
    desc: 'Cuatro preguntas cortitas, nada complicado.',
  },
  {
    n: '2',
    title: 'Te presentamos gente de confianza',
    desc: 'Notarías cercanas, verificadas y bien calificadas.',
  },
  {
    n: '3',
    title: 'Da el paso cuando quieras',
    desc: 'Agendamos tu cita en el horario que mejor te acomode.',
  },
];

/* ── FAQ ──────────────────────────────────────────────────────── */
const FAQ = [
  {
    q: '¿Cuánto cuesta un testamento?',
    a: 'Menos de lo que imaginas, y en septiembre (Mes del Testamento) suele costar aún menos. El monto exacto depende de tu caso — la notaría te lo dice de inmediato.',
  },
  {
    q: '¿Qué pasa si no dejo testamento?',
    a: 'La ley decide cómo se reparten tus bienes, no tú, y el trámite se vuelve más largo para tu familia. Una notaría te explica qué significa eso en tu situación.',
  },
  {
    q: '¿Cuánto tiempo toma el trámite?',
    a: 'Normalmente se resuelve en una sola cita. Agenda con una notaría verificada y sal ese mismo día con todo en orden.',
  },
];

/* ── Head metadata (mirrors App.jsx syncHeadMetadata pattern) ──── */
const syncHeadMetadata = ({ title, description, url }) => {
  document.title = title;

  const metaConfigs = [
    { attr: 'name', key: 'description', content: description },
    { attr: 'property', key: 'og:title', content: title },
    { attr: 'property', key: 'og:description', content: description },
    { attr: 'property', key: 'og:type', content: 'website' },
    { attr: 'property', key: 'og:url', content: url },
    { attr: 'name', key: 'twitter:card', content: 'summary' },
    { attr: 'name', key: 'twitter:title', content: title },
    { attr: 'name', key: 'twitter:description', content: description },
  ];

  metaConfigs.forEach(({ attr, key, content }) => {
    let element = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attr, key);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  });

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
};

export const TestamentoFacilLanding = () => {
  useEffect(() => {
    const prevTitle = document.title;
    syncHeadMetadata({
      title: 'TestamentoFácil | Con la notaría indicada, sin vueltas — by Mindware',
      description:
        'En México, los testamentos se tramitan ante notaría. Responde unas breves preguntas y te conectamos gratis con la notaría verificada adecuada, cerca de ti.',
      url: `${window.location.origin}/testamento-facil`,
    });
    return () => { document.title = prevTitle; };
  }, []);

  return (
    <div className="tf-page">

      {/* ── Nav ── */}
      <nav className="tf-nav">
        <div className="tf-nav-inner">
          <a href="/testamento-facil" className="tf-nav-brand">
            <img src="img/logo-testamentofacil.png" alt="TestamentoFácil" className="tf-nav-logo" />
            <span>TestamentoFácil</span>
            <span className="tf-by">by Mindware</span>
          </a>
          <div className="tf-nav-links">
            <a href="#tf-how">Cómo funciona</a>
            <a href="#tf-faq">Preguntas frecuentes</a>
            <a href={BLOG_URL} target="_blank" rel="noreferrer">Blog</a>
            <a href={APP_URL} target="_blank" rel="noreferrer" className="tf-nav-cta">Comenzar</a>
          </div>
          <a href="/" className="tf-back">&#8592; Mindware</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="tf-hero">
        <div className="tf-hero-bg" />
        <div className="tf-hero-grid" />
        <div className="tf-wrap tf-hero-inner">
          <div className="tf-hero-copy">
            <div className="tf-hero-badge">
              <span className="tf-badge-dot" />
              Estamos contigo en cada paso
            </div>
            <span className="tf-eyebrow">Trámite de testamento en México</span>
            <h1>
              Con la notaría indicada,
              <br />
              <span className="tf-grad">sin vueltas.</span>
            </h1>
            <p>
              En México, los testamentos se tramitan ante notaría. Responde{' '}
              <strong>unas breves preguntas</strong> y te damos la información
              para iniciar tu trámite con la notaría adecuada, cerquita de ti.
            </p>
            <div className="tf-hero-actions">
              <a href={APP_URL} target="_blank" rel="noreferrer" className="tf-btn-primary">
                Comenzar <i className="fa fa-arrow-right" />
              </a>
              <a href="#tf-how" className="tf-btn-ghost">Cómo funciona</a>
            </div>
            <div className="tf-hero-meta">
              <span><i className="fa fa-clock-o" /> Toma menos de 3 minutos</span>
              <span><i className="fa fa-gift" /> Sin costo</span>
              <span><i className="fa fa-lock" /> Información privada y segura</span>
            </div>
          </div>

          <div className="tf-hero-visual">
            <div className="tf-blog-card">
              <div className="tf-blog-bar">
                <span><i className="fa fa-file-text-o" aria-hidden="true" /> Del blog</span>
                <a href={BLOG_URL} target="_blank" rel="noreferrer">Ver todos →</a>
              </div>
              <div className="tf-blog-list">
                {BLOG_POSTS.map((post) => (
                  <a
                    key={post.href}
                    href={post.href}
                    target="_blank"
                    rel="noreferrer"
                    className="tf-blog-item"
                  >
                    <span className="tf-blog-item-title">{post.title}</span>
                    <span className="tf-blog-item-date">{post.date}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* ── Testimonial ── */}
        <section className="tf-section tf-testimonial-section">
          <div className="tf-wrap">
            <div className="tf-testimonial-card">
              <div className="tf-testimonial-avatar">RM</div>
              <blockquote>&ldquo;Fue mucho más sencillo de lo que pensaba.&rdquo;</blockquote>
              <cite>Rosa M., Guadalajara</cite>
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section id="tf-how" className="tf-section tf-how-section">
          <div className="tf-wrap">
            <div className="tf-section-head">
              <span className="tf-label">Cómo funciona</span>
              <h2>Tres pasos, sin vueltas</h2>
              <p>
                En México, los testamentos se tramitan ante una notaría. Con
                unas breves preguntas te damos justo lo que necesitas para
                iniciar el trámite con la notaría adecuada para ti.
              </p>
            </div>
            <div className="tf-steps3">
              {STEPS.map((s) => (
                <div key={s.n} className="tf-step3">
                  <span className="tf-step3-num">{s.n}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="tf-faq" className="tf-section tf-faq-section">
          <div className="tf-wrap tf-faq-wrap">
            <div className="tf-section-head">
              <span className="tf-label">Preguntas frecuentes</span>
              <h2>Dudas que la gente se hace</h2>
              <p>Y cuando quieras respuestas para tu caso, una notaría te lo aclara en minutos.</p>
            </div>
            <div className="tf-faq-list">
              {FAQ.map((item, i) => (
                <details key={item.q} className="tf-faq-item">
                  <summary>
                    <span className="tf-faq-icon">{i + 1}</span>
                    <span className="tf-faq-q-text">{item.q}</span>
                  </summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── Footer CTA ── */}
        <section className="tf-section tf-cta-section">
          <div className="tf-wrap tf-cta-inner">
            <span className="tf-label">Comenzar</span>
            <h2>Da el primer paso hoy.</h2>
            <p>
              Responde unas preguntas breves y te conectamos, sin costo, con
              la notaría indicada para ti.
            </p>
            <div className="tf-cta-actions">
              <a href={APP_URL} target="_blank" rel="noreferrer" className="tf-btn-primary tf-btn-lg">
                Comenzar ahora <i className="fa fa-arrow-right" />
              </a>
            </div>
            <div className="tf-cta-sub">
              <a href="mailto:contact@mindware.com.mx">Preguntas? contact@mindware.com.mx</a>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="tf-footer">
        <div className="tf-wrap tf-footer-inner">
          <span>TestamentoFácil by <a href="/">Mindware</a></span>
          <div className="tf-footer-links">
            <a href={NOTARY_REGISTER_URL} target="_blank" rel="noreferrer">¿Eres notaría? Regístrate aquí →</a>
            <a href={BLOG_URL} target="_blank" rel="noreferrer">Blog</a>
            <a href="/#products">Otros productos</a>
            <a href="/">&#8592; Volver a Mindware</a>
          </div>
        </div>
      </footer>

    </div>
  );
};
